import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { enqueueSnackbar } from 'notistack';

import { createKanbanTicket } from '../thunks/createKanbanTicket.thunk';
import { deleteKanbanTicket } from '../thunks/deleteKanbanTicket.thunk';
import { loadKanbanTickets } from '../thunks/loadKanbanTickets.thunk';
import { moveKanbanTicket } from '../thunks/moveKanbanTicket.thunk';
import { updateKanbanTicket } from '../thunks/updateKanbanTicket.thunk';

export const UNASSIGNED_ASSIGNEE_FILTER = 'UNASSIGNED';

export const kanbanTicketsAdapter = createEntityAdapter();

const initialState = {
  tickets: kanbanTicketsAdapter.getInitialState(),
  loading: false,
  loaded: false,
  saving: false,
  deleting: false,
  assigneeFilter: null,
  assignmentFilter: null,
  // ticketId -> requestId of the latest optimistic move; late responses of
  // superseded moves are ignored so rapid re-drags never flicker or clobber.
  pendingMoveRequestIds: {},
};

export const kanbanSlice = createSlice({
  name: 'kanban',
  initialState,
  reducers: {
    resetKanban: () => initialState,
    setAssigneeFilter: (state, action) => {
      state.assigneeFilter = action.payload;
    },
    setAssignmentFilter: (state, action) => {
      state.assignmentFilter = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(loadKanbanTickets.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadKanbanTickets.fulfilled, (state, action) => {
        kanbanTicketsAdapter.setAll(state.tickets, action.payload.tickets);
        state.loading = false;
        state.loaded = true;
      })
      .addCase(loadKanbanTickets.rejected, (state) => {
        state.loading = false;
        state.loaded = true;
        enqueueSnackbar(i18n.t('kanban.load_failed'), { variant: 'error' });
      })
      .addCase(createKanbanTicket.pending, (state) => {
        state.saving = true;
      })
      .addCase(createKanbanTicket.fulfilled, (state, action) => {
        kanbanTicketsAdapter.addOne(state.tickets, action.payload);
        state.saving = false;
        enqueueSnackbar(i18n.t('kanban.created'), { variant: 'success' });
      })
      .addCase(createKanbanTicket.rejected, (state) => {
        state.saving = false;
        enqueueSnackbar(i18n.t('kanban.save_failed'), { variant: 'error' });
      })
      .addCase(updateKanbanTicket.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateKanbanTicket.fulfilled, (state, action) => {
        // setOne, not upsertOne: the API returns the complete item and a
        // merge would resurrect attributes the update REMOVEd (cleared
        // assignee/assignment/description).
        kanbanTicketsAdapter.setOne(state.tickets, action.payload);
        state.saving = false;
        enqueueSnackbar(i18n.t('kanban.updated'), { variant: 'success' });
      })
      .addCase(updateKanbanTicket.rejected, (state) => {
        state.saving = false;
        enqueueSnackbar(i18n.t('kanban.save_failed'), { variant: 'error' });
      })
      .addCase(moveKanbanTicket.pending, (state, action) => {
        const { ticketId, changes } = action.meta.arg;
        state.pendingMoveRequestIds[ticketId] = action.meta.requestId;
        kanbanTicketsAdapter.updateOne(state.tickets, {
          id: ticketId,
          changes,
        });
      })
      .addCase(moveKanbanTicket.fulfilled, (state, action) => {
        const { ticketId } = action.meta.arg;
        if (state.pendingMoveRequestIds[ticketId] !== action.meta.requestId) {
          return;
        }
        delete state.pendingMoveRequestIds[ticketId];
        kanbanTicketsAdapter.setOne(state.tickets, action.payload);
      })
      .addCase(moveKanbanTicket.rejected, (state, action) => {
        const { ticketId, previous } = action.meta.arg;
        if (state.pendingMoveRequestIds[ticketId] !== action.meta.requestId) {
          // A newer move owns this ticket; its outcome subsumes this one.
          return;
        }
        enqueueSnackbar(i18n.t('kanban.move_failed'), { variant: 'error' });
        delete state.pendingMoveRequestIds[ticketId];
        kanbanTicketsAdapter.updateOne(state.tickets, {
          id: ticketId,
          changes: previous,
        });
      })
      .addCase(deleteKanbanTicket.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteKanbanTicket.fulfilled, (state, action) => {
        // Drop any pending move so its late response cannot resurrect the
        // deleted ticket via the move handlers.
        delete state.pendingMoveRequestIds[action.meta.arg.ticketId];
        kanbanTicketsAdapter.removeOne(state.tickets, action.meta.arg.ticketId);
        state.deleting = false;
        enqueueSnackbar(i18n.t('kanban.deleted'), { variant: 'success' });
      })
      .addCase(deleteKanbanTicket.rejected, (state) => {
        state.deleting = false;
        enqueueSnackbar(i18n.t('kanban.delete_failed'), { variant: 'error' });
      });
  },
});

export const { resetKanban, setAssigneeFilter, setAssignmentFilter } =
  kanbanSlice.actions;

export default kanbanSlice.reducer;
